import * as React from 'react';
import styled from 'styled-components';

import * as colors from '../../shared/colors';
import PlusMinus from '../../shared/components/icons/PlusMinus';
import TaskIcon from '../../shared/components/icons/TaskIcon';
import { formatYMD, parseDate } from '../../shared/dateFns';
import t from '../../shared/translations';
import DownloadAnswers from '../myAnswers/DownloadAnswers';
import { IFinishedPath } from './MyHistory';
import { AccordionBody, AccordionContainer, AccordionHeader, AccordionHeading, SectionHeading } from './stylings';

interface IProps {
    finishedPaths: IFinishedPath[];
}

const Miles = styled.span`
    flex-shrink: 0;
`;

const DatesHolder = styled.div`
    display: flex;
    & > div {
        width: 50%;
    }
    margin-bottom: 0.5rem;
`;

const TaskTable = styled.table`
    border: solid 1px ${colors.secondaryColor};
    thead {
        background-color: ${colors.secondaryColor};
    }
    svg {
        width: 30px;
        height: 30px;
        margin-right: 0.3rem;
    }
`;

const TaskA = styled.a`
    display: flex;
    align-items: center;
`;

function FinishedPaths({ finishedPaths }: IProps): JSX.Element {
    const [open, setOpen] = React.useState('');
    const toggle = (id: string) => {
        setOpen(currId => {
            if (currId === id) {
                return '';
            } else {
                return id;
            }
        });
    };
    if (finishedPaths.length === 0) {
        return null;
    }
    return <section>
        <SectionHeading>{t('player.my-history.heading-finished')}</SectionHeading>
        <div>
            {finishedPaths.map(path => {
                const miles = path.acceptedItems.reduce((acc, item) => acc + item.earnedMiles, 0);
                return <AccordionContainer key={path.id}>
                    <AccordionHeader onClick={() => toggle(path.id)} $pointer={true}>
                        <AccordionHeading>{path.pathname}</AccordionHeading>
                        <Miles>{miles}</Miles>
                        <PlusMinus open={open === path.id} />
                    </AccordionHeader>
                    {open === path.id &&
                        <AccordionBody>
                            <div>
                                {t('player.my-history.label-miles-in-path', { miles })}
                            </div>
                            <DatesHolder>
                                <div>
                                    {t('player.my-history.label-start-date', { date: path.startDate })}
                                </div>
                                <div>
                                    {t('player.my-history.label-end-date', { date: path.endDate })}
                                </div>
                            </DatesHolder>
                            {path.acceptedItems.length > 0 && <>
                                <AccordionHeading>{t('player.my-history.heading-done-tasks')}</AccordionHeading>
                                <TaskTable>
                                    <thead>
                                        <tr>
                                            <th>
                                                {t('player.my-history.table-tasks-heading-task')}
                                            </th>
                                            <th>
                                                {t('player.my-history.table-tasks-heading-finish-date')}
                                            </th>
                                            <th>
                                                {t('player.my-history.table-tasks-heading-miles')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {path.acceptedItems
                                            .filter(i => i.type !== 'event')
                                            .map(item =>
                                                <tr key={item.id}>
                                                    <td>
                                                        <TaskA href={item.url}>
                                                            <TaskIcon icon={item.icon} active={true} />
                                                            {item.title}
                                                        </TaskA>
                                                    </td>
                                                    <td>
                                                        {formatYMD(parseDate(item.finishDatetime))}
                                                    </td>
                                                    <td>
                                                        {item.earnedMiles}
                                                    </td>
                                                </tr>)}
                                    </tbody>
                                </TaskTable>
                            </>}
                            <DownloadAnswers pathId={path.id} />
                        </AccordionBody>
                    }
                </AccordionContainer>;
            })}
        </div>
    </section>;
}

export default React.memo(FinishedPaths);
